import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { css } from '@sorare/css';

import { Rarity } from '#src/__generated__/globalTypes.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelM, LabelS } from '#src/atoms/typography/index.tsx';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';
import { glossary } from '#src/lib/glossary.ts';
import { getRewardImageUrl } from '#src/lib/rewards.tsx';

import cashBg from './assets/cash-bg.webp';
import creditBg from './assets/credit-bg.webp';
import essenceBgLimited from './assets/essence-bg-limited.webp';
import essenceBgRare from './assets/essence-bg-rare.webp';
import essenceBgSuperRare from './assets/essence-bg-super_rare.webp';
import essenceBgUnique from './assets/essence-bg-unique.webp';

const list = css`
  width: 100%;
  padding: var(--triple-unit);
`;

const boost = css`
  width: 100%;
  padding: var(--intermediate-unit) var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid transparent;
`;

const BOOSTED_LABEL_GRADIENT =
  'linear-gradient(323deg, #F1FF00 -3.1%, #3DDC84 52.7%)';

const getEssenceBackground = (rarity: Rarity) => {
  switch (rarity) {
    case Rarity.rare:
      return `url(${essenceBgRare}) no-repeat center/cover padding-box, linear-gradient(0deg, #800000 0%, #EA000C 100%) border-box`;
    case Rarity.super_rare:
      return `url(${essenceBgSuperRare}) no-repeat center/cover padding-box, linear-gradient(180deg, #179EFF 0%, #0A42D0 100%) border-box`;
    case Rarity.unique:
      return `url(${essenceBgUnique}) no-repeat center/cover padding-box, linear-gradient(0deg, #29073C 0%, #AC11FF 100%) border-box`;
    default:
      return `url(${essenceBgLimited}) no-repeat center/cover padding-box, linear-gradient(0deg, #E67C00 0%, #F7B100 100%) border-box`;
  }
};

type Props = {
  rarity: Rarity;
  footnote?: ReactNode;
};

export const BoostedRewardsList = ({ rarity, footnote }: Props) => {
  const boostedRewards = [
    {
      key: 'cash',
      title: <FormattedMessage {...glossary.cashRewards} />,
      image: `${FRONTEND_ASSET_HOST}/rewards/cash.png`,
      background: `url(${cashBg}) no-repeat center/cover padding-box, linear-gradient(82deg, #CC8CD6 -11.67%, #BE92D4 32.51%, #A28BD7 54.72%, #98DDF4 100%) border-box`,
    },
    {
      key: 'credit',
      title: (
        <FormattedMessage
          defaultMessage="Market Credit Rewards{asterisk}"
          values={{ asterisk: footnote ? ' *' : '' }}
        />
      ),
      image: `${FRONTEND_ASSET_HOST}/rewards/credit.png`,
      background: `url(${creditBg}) no-repeat center/cover padding-box, linear-gradient(323deg, #f1ff00 -3.1%, #3ddc84 52.7%) border-box`,
    },
    {
      key: 'essence',
      title: (
        <FormattedMessage
          defaultMessage="Essence Rewards{asterisk}"
          values={{ asterisk: footnote ? ' *' : '' }}
        />
      ),
      image: getRewardImageUrl('shard', rarity),
      background: getEssenceBackground(rarity),
    },
  ];

  return (
    <Vertical gap={2} center className={list}>
      {boostedRewards.map(({ key, title, image, background }) => (
        <Horizontal
          gap={1.5}
          key={key}
          className={boost}
          style={{ background }}
        >
          <img src={image} alt="" width={32} height={32} />
          <Vertical gap={0}>
            <LabelS
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': BOOSTED_LABEL_GRADIENT,
                } as CSSProperties
              }
            >
              <FormattedMessage defaultMessage="Boosted" />
            </LabelS>
            <LabelM bold>{title}</LabelM>
          </Vertical>
        </Horizontal>
      ))}
      {!!footnote && <LabelM color="var(--c-nd-600)">{footnote}</LabelM>}
    </Vertical>
  );
};
